import { Injectable } from '@angular/core';
import type { AssociatedUser } from '@vendasta/protos/business_center/v1/types';
import { listAllUsersForBusiness$ } from '@vendasta/users';
import { Observable, shareReplay, tap } from 'rxjs';

const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  users$: Observable<AssociatedUser[]>;
  expiresAt: number;
}

interface NamedUser {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  userId?: string | null;
}

/** Full name, falling back to email and then user ID. */
export function userDisplayName(user: NamedUser): string {
  return [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email || user.userId || '';
}

/** Each business's users, shared across conversations so switching conversations does not refetch them. */
@Injectable({ providedIn: 'root' })
export class InboxBusinessUsersService {
  private readonly cache = new Map<string, CacheEntry>();

  users$(accountGroupId: string): Observable<AssociatedUser[]> {
    const cached = this.cache.get(accountGroupId);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.users$;
    }
    const users$ = listAllUsersForBusiness$(accountGroupId).pipe(
      tap({ error: () => this.cache.delete(accountGroupId) }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.cache.set(accountGroupId, { users$, expiresAt: Date.now() + CACHE_TTL_MS });
    return users$;
  }
}
